import React, { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../components/common";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, errMsg, fmtINR, fmtTimeIST, istDateISO } from "../../lib/api";
import { useLive } from "../../lib/useLive";
import { VehicleTypeBadge, modeLabel } from "../../components/parking/common";
import { ReceiptDialog } from "../../components/parking/Ticket";
import { useMarshal } from "./marshalShared";

/* Phase 202 — Log: entry / exit events at this location and the receipts I collected, per day. */

export default function MarshalLog() {
  const { code } = useMarshal();
  const live = useLive(code, "marshal");
  const [day, setDay] = useState(istDateISO());
  const [data, setData] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const load = useCallback(() => api.get(`/t/${code}/parking/marshal/logs?day=${day}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [code, day]);
  useEffect(() => { load(); }, [load, live]);
  const total = (data?.my_receipts || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  return (
    <div className="space-y-4" data-testid="marshal-log">
      <div className="flex items-center justify-between gap-2">
        <Input type="date" value={day} max={istDateISO()} onChange={(e) => setDay(e.target.value)} className="h-11 w-[170px]" data-testid="log-day" />
        <span className="text-sm text-muted-foreground">Collected <span className="font-semibold text-foreground tabular-nums" data-testid="log-total">{fmtINR(total)}</span></span>
      </div>
      {!data ? <Spinner /> : (
        <Tabs defaultValue="events">
          <TabsList className="w-full"><TabsTrigger value="events" className="flex-1" data-testid="log-tab-events">Entries / exits <Badge variant="secondary" className="ml-1.5">{data.entry_logs.length}</Badge></TabsTrigger><TabsTrigger value="receipts" className="flex-1" data-testid="log-tab-receipts">My receipts <Badge variant="secondary" className="ml-1.5">{data.my_receipts.length}</Badge></TabsTrigger></TabsList>
          <TabsContent value="events">
            {data.entry_logs.length === 0 ? <EmptyState title="No entries or exits yet" subtitle="Every check-in and check-out at this location is listed here." /> : (
              <div className="space-y-1.5" data-testid="log-events">
                {data.entry_logs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm" data-testid={`log-event-${l.id}`}>
                    <span className="flex items-center gap-2 min-w-0"><span className={`h-7 w-7 rounded-full inline-flex items-center justify-center shrink-0 ${l.event === "entry" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground"}`}>{l.event === "entry" ? <LogIn className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}</span><VehicleTypeBadge type={l.vehicle_type} short /><span className="font-mono font-medium truncate">{l.vehicle_number}</span></span>
                    <span className="text-xs text-muted-foreground shrink-0 text-right"><span className="capitalize font-medium text-foreground">{l.event}</span> {fmtTimeIST(l.at)}<br />{l.marshal_name || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="receipts">
            {data.my_receipts.length === 0 ? <EmptyState title="No receipts collected" subtitle="POS / UPI / cash you collect appears here." /> : (
              <div className="space-y-1.5" data-testid="log-receipts">
                {data.my_receipts.map((p) => (
                  <button key={p.id} type="button" onClick={() => setReceipt(p)} data-testid={`log-receipt-${p.receipt_no}`} className="w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm text-left hover:bg-accent/50">
                    <span className="min-w-0"><span className="font-mono text-xs">{p.receipt_no}</span><br /><span className="text-xs text-muted-foreground"><Receipt className="h-3 w-3 inline mr-1" />{p.vehicle_number} · <span className="capitalize">{p.kind}</span> · {modeLabel(p.mode)} · {fmtTimeIST(p.paid_at)}</span></span>
                    <span className="font-semibold tabular-nums shrink-0">{fmtINR(p.amount)}</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
      <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
